import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import TablePaginationFooter from "./TablePaginationFooter";
import { formatCryptoAmount } from "../../utils/tableUtils";
import styles from "../../pages/css/Dashboard.module.css";

const getCurrencyColor = (token) => {
  switch (token?.toUpperCase()) {
    case "USDT":
      return styles.badgeUsdt;
    case "BTC":
      return styles.badgeBtc;
    case "ETH":
      return styles.badgeEth;
    case "USD":
      return styles.badgeUsd;
    default:
      return styles.badgeDefault;
  }
};

const RecentTransactionsTable = ({
  displayedTransactions,
  totalTransactionsCount,
  isLoading,
  copiedText,
  handleCopy,
  sectionLeftVariants,
  tableBodyVariants,
  tableRowVariants,
  textFadeUpVariants,
}) => {
  return (
    <motion.div
      className={`col-12 col-lg-8 ${styles.leftTableCol}`}
      variants={sectionLeftVariants}
      initial="hidden"
      animate={isLoading ? "hidden" : "visible"}
    >
      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <div>
            <motion.h5 className={styles.tableCardTitle} variants={textFadeUpVariants}>
              Recent Transactions
            </motion.h5>
            <motion.p className={styles.tableCardSubtitle} variants={textFadeUpVariants}>
              Real-time payment gateway ledger
            </motion.p>
          </div>
          <Link to="/deposit_transactions" className={styles.btnHeaderIcon} title="View All Transactions">
            <i className="fas fa-table"></i>
          </Link>
        </div>

        <div className="card-body p-0">
          <div className={`table-responsive ${styles.tableResponsiveFix}`}>
            <table className={`${styles.customTable} text-center align-middle`}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Currency</th>
                  <th>From</th>
                  <th>Hash</th>
                </tr>
              </thead>

              {isLoading ? (
                <tbody>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx}>
                      <td><span className={styles.skeletonBox} style={{ width: "24px" }}></span></td>
                      <td><span className={styles.skeletonBox} style={{ width: "110px" }}></span></td>
                      <td><span className={styles.skeletonBox} style={{ width: "60px" }}></span></td>
                      <td><span className={styles.skeletonBox} style={{ width: "95px", borderRadius: "9999px" }}></span></td>
                      <td><span className={styles.skeletonBox} style={{ width: "110px" }}></span></td>
                      <td><span className={styles.skeletonBox} style={{ width: "110px" }}></span></td>
                    </tr>
                  ))}
                </tbody>
              ) : displayedTransactions.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan="6" className="text-center py-4" style={{ color: "#A3A3A3" }}>
                      <i className="fas fa-inbox mb-2 d-block" style={{ fontSize: "1.8rem", opacity: 0.5 }}></i>
                      No Transactions Found.
                    </td>
                  </tr>
                </tbody>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.tbody
                    variants={tableBodyVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {displayedTransactions.map((trx, index) => {
                      const rowIndex = index + 1;
                      const fromAbbr = trx.from ? `${trx.from.slice(0, 8)}...${trx.from.slice(-4)}` : "N/A";
                      const hashAbbr = trx.transactionHash ? `${trx.transactionHash.slice(0, 8)}...${trx.transactionHash.slice(-4)}` : "N/A";

                      return (
                        <motion.tr
                          key={trx.id || index}
                          variants={tableRowVariants}
                        >
                          <td className="text-nowrap" style={{ fontWeight: "600", color: "#A3A3A3" }}>
                            {rowIndex}
                          </td>

                          <td className="text-nowrap" style={{ color: "#A3A3A3" }} title={moment(trx.createdAt).format("DD MMM YYYY, hh:mm A")}>
                            {moment(trx.createdAt).format("DD MMM YYYY")}
                          </td>

                          <td className="text-nowrap font-weight-bold">
                            <span style={{ color: "#FFFFFF", fontWeight: "600" }}>
                              {trx.type || "Deposit"}
                            </span>
                          </td>

                          <td className="text-nowrap">
                            <span className={`${styles.badgeCustom} ${getCurrencyColor(trx.tokenName)}`}>
                              {formatCryptoAmount(trx.amount)} {trx.tokenName || ""}
                            </span>
                          </td>

                          <td className="text-nowrap">
                            <div className={styles.addressCell}>
                              <a
                                href={`https://bscscan.com/address/${trx.from}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.fromLink}
                                title={trx.from || "Wallet Address"}
                              >
                                {fromAbbr}
                              </a>
                              {trx.from && (
                                <button
                                  type="button"
                                  className={styles.btnCopy}
                                  onClick={(e) => handleCopy(trx.from, e)}
                                  title="Copy full address"
                                >
                                  <i className={`fas ${copiedText === trx.from ? "fa-check text-success" : "fa-copy"}`}></i>
                                </button>
                              )}
                            </div>
                          </td>

                          <td className="text-nowrap">
                            <div className={styles.addressCell}>
                              <a
                                href={`https://bscscan.com/tx/${trx.transactionHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.hashLink}
                                title={trx.transactionHash || "Tx Hash"}
                              >
                                {hashAbbr}
                              </a>
                              {trx.transactionHash && (
                                <button
                                  type="button"
                                  className={styles.btnCopy}
                                  onClick={(e) => handleCopy(trx.transactionHash, e)}
                                  title="Copy transaction hash"
                                >
                                  <i className={`fas ${copiedText === trx.transactionHash ? "fa-check text-success" : "fa-copy"}`}></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </motion.tbody>
                </AnimatePresence>
              )}
            </table>
          </div>
        </div>

        {/* Table Footer */}
        <TablePaginationFooter
          currentCount={displayedTransactions.length}
          totalCount={totalTransactionsCount}
          viewAllLink="/deposit_transactions"
          viewAllText="View All Transactions"
        />
      </div>
    </motion.div>
  );
};

export default RecentTransactionsTable;
